import React, { useState, useEffect } from 'react';
import { matchesAPI } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import { FaTrophy, FaEdit, FaCalendar, FaClock, FaCheck } from 'react-icons/fa';

const ResultsManagementSection = () => {
  const { language } = useLanguage();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMatch, setEditingMatch] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterGroup, setFilterGroup] = useState('all');
  const [filterRound, setFilterRound] = useState('all');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [filterLeg, setFilterLeg] = useState('all');
  const [showPDF, setShowPDF] = useState(false);
  const [activePdfUrl, setActivePdfUrl] = useState(null);

  const groups = [
    { id: 1, name: 'Poule 1', file: '/documents/calendrier/Poule 1.pdf' },
    { id: 2, name: 'Poule 2', file: '/documents/calendrier/poule 2.pdf' },
    { id: 3, name: 'Poule 3', file: '/documents/calendrier/poule 3.pdf' },
    { id: 4, name: 'Poule 4', file: '/documents/calendrier/poule 4.pdf' },
    { id: 5, name: 'Poule 5', file: '/documents/calendrier/Poule 5.pdf' },
    { id: 6, name: 'Poule 6', file: '/documents/calendrier/Poule 6.pdf' }
  ];

  useEffect(() => {
    loadMatches();
  }, []);

  const loadMatches = async () => {
    try {
      setLoading(true);
      const response = await matchesAPI.getAll();
      // Trier par date (plus récents en premier)
      const sorted = (response.data || []).sort((a, b) =>
        new Date(b.match_date) - new Date(a.match_date)
      );
      setMatches(sorted);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (match) => {
    setEditingMatch({
      ...match,
      match_date: match.match_date ? match.match_date.slice(0, 16) : ''
    });
  };

  const handleSave = async () => {
    try {
      await matchesAPI.update(editingMatch.id, editingMatch);
      setEditingMatch(null);
      loadMatches();
      alert('✅ Résultat enregistré avec succès!');
    } catch (error) {
      alert('❌ Erreur lors de l\'enregistrement');
      console.error(error);
    }
  };

  const handleQuickFinish = async (match) => {
    const homeScore = prompt(`Score ${match.home_team_name}:`, '0');
    if (homeScore === null) return;

    const awayScore = prompt(`Score ${match.away_team_name}:`, '0');
    if (awayScore === null) return;

    try {
      await matchesAPI.update(match.id, {
        ...match,
        home_score: parseInt(homeScore),
        away_score: parseInt(awayScore),
        status: 'finished'
      });
      loadMatches();
      alert('✅ Résultat ajouté!');
    } catch (error) {
      alert('❌ Erreur');
    }
  };

  // Filtrer les matchs
  const filteredMatches = matches.filter(m => {
    if (filterStatus !== 'all' && m.status !== filterStatus) return false;
    if (filterGroup !== 'all') {
      const groupNum = parseInt(filterGroup);
      if (m.home_team_group !== groupNum && m.away_team_group !== groupNum) return false;
    }
    if (filterRound !== 'all') {
      const r = parseInt(filterRound, 10);
      if (!m.round || parseInt(m.round, 10) !== r) return false;
    }
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      const a = (m.away_team_name || '').toLowerCase();
      const h = (m.home_team_name || '').toLowerCase();
      if (!h.includes(q) && !a.includes(q)) return false;
    }
    if (filterDate) {
      if (new Date(m.match_date).toISOString().slice(0, 10) !== filterDate) return false;
    }
    if (filterLeg !== 'all' && m.leg !== filterLeg) return false;
    return true;
  });

  // Grouper par journée (الجولة) pour l'affichage
  const matchesByRound = {};
  filteredMatches.forEach(m => {
    const r = m.round ? parseInt(m.round, 10) : 0;
    if (!matchesByRound[r]) matchesByRound[r] = [];
    matchesByRound[r].push(m);
  });
  Object.keys(matchesByRound).forEach(r => {
    matchesByRound[r].sort((a, b) => new Date(a.match_date) - new Date(b.match_date));
  });
  const roundsWithData = Object.keys(matchesByRound).map(Number).filter(r => r > 0).sort((a, b) => a - b);
  const rounds = matchesByRound[0]?.length ? [...roundsWithData, 0] : roundsWithData;

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <h1 style={styles.title}>🏆 {language === 'ar' ? 'إدارة النتائج' : 'Gestion des Résultats'}</h1>

      <div style={styles.infoBox}>
        <p>💡 {language === 'ar' ? 'أضف نتائج المباريات بسرعة وعدّل التواريخ عند الحاجة (طقس، إلخ)' : 'Ajoutez rapidement les résultats des matchs et modifiez les dates si nécessaire (météo, etc.)'}</p>
        <div style={{ marginTop: '10px', display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button 
            onClick={() => setShowPDF(!showPDF)}
            style={{ ...styles.pdfBtn, background: showPDF ? '#c41e3a' : '#1a365d' }}
          >
            <FaCalendar /> {showPDF ? (language === 'ar' ? 'إغلاق الرزنامة' : 'Fermer la Référence') : (language === 'ar' ? 'عرض الرزنامة (PDF)' : 'Voir la Référence (PDF)')}
          </button>
          <p style={{ color: '#856404', fontWeight: 'bold', margin: 0 }}>
            ⚠️ {language === 'ar' ? 'تعديل النتائج هنا يحدث نفس قاعدة بيانات قسم "المباريات".' : 'Modifier les résultats ici met à jour la même base de données que la section "Matchs".'}
          </p>
        </div>
      </div>

      {showPDF && (
        <div style={styles.pdfSection}>
          <div style={styles.pdfButtons}>
            {groups.map(g => (
              <button 
                key={g.id}
                onClick={() => setActivePdfUrl(g.file)}
                style={{
                  ...styles.pdfGroupBtn,
                  background: activePdfUrl === g.file ? '#1a365d' : '#f5f5f5',
                  color: activePdfUrl === g.file ? 'white' : '#333'
                }}
              >
                {g.name}
              </button>
            ))}
          </div>
          {activePdfUrl && (
            <div style={styles.pdfFrameContainer}>
              <iframe 
                src={`${activePdfUrl}#toolbar=0`}
                width="100%"
                height="500px"
                title="Calendrier PDF"
                style={{ border: 'none', borderRadius: '8px' }}
              />
            </div>
          )}
        </div>
      )}

      {/* Filtres */}
      <div style={styles.filtersCard}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>{language === 'ar' ? 'المرحلة' : 'Phase'}:</label>
          <div style={{ display: 'flex', gap: '5px' }}>
            <button
              onClick={() => setFilterLeg('all')}
              style={{
                ...styles.phaseToggle,
                background: filterLeg === 'all' ? '#1a365d' : '#eee',
                color: filterLeg === 'all' ? 'white' : '#333'
              }}
            >
              {language === 'ar' ? 'الكل' : 'Tous'}
            </button>
            <button
              onClick={() => setFilterLeg('aller')}
              style={{
                ...styles.phaseToggle,
                background: filterLeg === 'aller' ? '#2e7d32' : '#eee',
                color: filterLeg === 'aller' ? 'white' : '#333'
              }}
            >
              {language === 'ar' ? 'ذهاب' : 'Aller'}
            </button>
            <button
              onClick={() => setFilterLeg('retour')}
              style={{
                ...styles.phaseToggle,
                background: filterLeg === 'retour' ? '#c41e3a' : '#eee',
                color: filterLeg === 'retour' ? 'white' : '#333'
              }}
            >
              {language === 'ar' ? 'إياب' : 'Retour'}
            </button>
          </div>
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>{language === 'ar' ? 'بحث باسم الفريق' : 'Recherche équipe'}:</label>
          <input
            type="text"
            placeholder={language === 'ar' ? 'بحث...' : 'Rechercher...'}
            value={filterSearch}
            onChange={(e) => setFilterSearch(e.target.value)}
            style={styles.filterInput}
          />
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>{language === 'ar' ? 'المجموعة' : 'Groupe'}:</label>
          <select
            value={filterGroup}
            onChange={(e) => setFilterGroup(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">{language === 'ar' ? 'الكل' : 'Tous'}</option>
            {[1, 2, 3, 4, 5, 6].map(g => (
              <option key={g} value={g}>{language === 'ar' ? `المجموعة ${g}` : `Groupe ${g}`}</option>
            ))}
          </select>
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>{language === 'ar' ? 'الجولة' : 'Journée'}:</label>
          <select
            value={filterRound}
            onChange={(e) => setFilterRound(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">{language === 'ar' ? 'الكل' : 'Tous'}</option>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22].map(r => (
              <option key={r} value={r}>{language === 'ar' ? `الجولة ${r}` : `J${r}`}</option>
            ))}
          </select>
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>{language === 'ar' ? 'الحالة' : 'Statut'}:</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">{language === 'ar' ? 'الكل' : 'Tous'}</option>
            <option value="upcoming">{language === 'ar' ? 'قادمة' : 'À venir'}</option>
            <option value="live">{language === 'ar' ? 'مباشر' : 'En direct'}</option>
            <option value="finished">{language === 'ar' ? 'منتهية' : 'Terminés'}</option>
          </select>
        </div>



        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>{language === 'ar' ? 'التاريخ' : 'Date'}:</label>
          <input
            type="date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            style={styles.filterInput}
          />
        </div>

        <div style={styles.statsGroup}>
          <div style={styles.stat}>
            <span style={styles.statLabel}>{language === 'ar' ? 'المجموع' : 'Total'}:</span>
            <span style={styles.statValue}>{filteredMatches.length}</span>
          </div>
          <div style={styles.stat}>
            <span style={styles.statLabel}>{language === 'ar' ? 'قادمة' : 'À venir'}:</span>
            <span style={styles.statValue}>{filteredMatches.filter(m => m.status === 'upcoming').length}</span>
          </div>
          <div style={styles.stat}>
            <span style={styles.statLabel}>{language === 'ar' ? 'منتهية' : 'Terminés'}:</span>
            <span style={styles.statValue}>{filteredMatches.filter(m => m.status === 'finished').length}</span>
          </div>
        </div>
      </div>

      {/* Modal d'édition */}
      {editingMatch && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2 style={styles.modalTitle}>Modifier le match</h2>

            <div style={styles.matchHeader}>
              <div style={styles.teamDisplay}>
                <strong>{editingMatch.home_team_name}</strong>
                <span style={styles.vs}>VS</span>
                <strong>{editingMatch.away_team_name}</strong>
              </div>
            </div>

            <div style={styles.form}>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Date et heure *</label>
                  <input
                    type="datetime-local"
                    value={editingMatch.match_date}
                    onChange={(e) => setEditingMatch({ ...editingMatch, match_date: e.target.value })}
                    style={styles.input}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Journée</label>
                  <input
                    type="number"
                    value={editingMatch.round || ''}
                    onChange={(e) => setEditingMatch({ ...editingMatch, round: e.target.value })}
                    style={styles.input}
                    min="1"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{language === 'ar' ? 'المرحلة' : 'Phase (Aller/Retour)'}</label>
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <button
                      type="button"
                      onClick={() => setEditingMatch({ ...editingMatch, leg: 'aller' })}
                      style={{
                        ...styles.legBtn,
                        background: (editingMatch.leg === 'aller' || !editingMatch.leg) ? '#1a365d' : '#eee',
                        color: (editingMatch.leg === 'aller' || !editingMatch.leg) ? 'white' : '#333',
                        border: (editingMatch.leg === 'aller' || !editingMatch.leg) ? 'none' : '1px solid #ddd'
                      }}
                    >
                      {language === 'ar' ? 'ذهاب (Aller)' : 'Aller'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingMatch({ ...editingMatch, leg: 'retour' })}
                      style={{
                        ...styles.legBtn,
                        background: editingMatch.leg === 'retour' ? '#c41e3a' : '#eee',
                        color: editingMatch.leg === 'retour' ? 'white' : '#333',
                        border: editingMatch.leg === 'retour' ? 'none' : '1px solid #ddd'
                      }}
                    >
                      {language === 'ar' ? 'إياب (Retour)' : 'Retour'}
                    </button>
                  </div>
                </div>
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Score {editingMatch.home_team_name}</label>
                  <input
                    type="number"
                    value={editingMatch.home_score || 0}
                    onChange={(e) => setEditingMatch({ ...editingMatch, home_score: e.target.value })}
                    style={styles.input}
                    min="0"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Score {editingMatch.away_team_name}</label>
                  <input
                    type="number"
                    value={editingMatch.away_score || 0}
                    onChange={(e) => setEditingMatch({ ...editingMatch, away_score: e.target.value })}
                    style={styles.input}
                    min="0"
                  />
                </div>
              </div>

              {/* Cartons Section */}
              <div style={styles.cardsSection}>
                <h3 style={styles.cardsSectionTitle}>🟨🟥 Cartons</h3>

                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>🟨 Cartons Jaunes {editingMatch.home_team_name}</label>
                    <input
                      type="number"
                      value={editingMatch.yellow_cards_home || 0}
                      onChange={(e) => setEditingMatch({ ...editingMatch, yellow_cards_home: e.target.value })}
                      style={styles.input}
                      min="0"
                    />
                    {parseInt(editingMatch.yellow_cards_home || 0) > 0 && (
                      <div style={{ marginTop: '8px' }}>
                        <label style={{ ...styles.label, fontSize: '12px', color: '#666' }}>Nom(s) de joueur</label>
                        <input
                          type="text"
                          placeholder="Ex: Ahmed Ben Ali, Mohamed Trabelsi"
                          value={editingMatch.yellow_card_players_home || ''}
                          onChange={(e) => setEditingMatch({ ...editingMatch, yellow_card_players_home: e.target.value })}
                          style={{ ...styles.input, fontSize: '13px' }}
                        />
                      </div>
                    )}
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>🟨 Cartons Jaunes {editingMatch.away_team_name}</label>
                    <input
                      type="number"
                      value={editingMatch.yellow_cards_away || 0}
                      onChange={(e) => setEditingMatch({ ...editingMatch, yellow_cards_away: e.target.value })}
                      style={styles.input}
                      min="0"
                    />
                    {parseInt(editingMatch.yellow_cards_away || 0) > 0 && (
                      <div style={{ marginTop: '8px' }}>
                        <label style={{ ...styles.label, fontSize: '12px', color: '#666' }}>Nom(s) de joueur</label>
                        <input
                          type="text"
                          placeholder="Ex: Ahmed Ben Ali, Mohamed Trabelsi"
                          value={editingMatch.yellow_card_players_away || ''}
                          onChange={(e) => setEditingMatch({ ...editingMatch, yellow_card_players_away: e.target.value })}
                          style={{ ...styles.input, fontSize: '13px' }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>🟥 Cartons Rouges {editingMatch.home_team_name}</label>
                    <input
                      type="number"
                      value={editingMatch.red_cards_home || 0}
                      onChange={(e) => setEditingMatch({ ...editingMatch, red_cards_home: e.target.value })}
                      style={styles.input}
                      min="0"
                    />
                    {parseInt(editingMatch.red_cards_home || 0) > 0 && (
                      <div style={{ marginTop: '8px' }}>
                        <label style={{ ...styles.label, fontSize: '12px', color: '#666' }}>Nom(s) de joueur</label>
                        <input
                          type="text"
                          placeholder="Ex: Ahmed Ben Ali, Mohamed Trabelsi"
                          value={editingMatch.red_card_players_home || ''}
                          onChange={(e) => setEditingMatch({ ...editingMatch, red_card_players_home: e.target.value })}
                          style={{ ...styles.input, fontSize: '13px' }}
                        />
                      </div>
                    )}
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>🟥 Cartons Rouges {editingMatch.away_team_name}</label>
                    <input
                      type="number"
                      value={editingMatch.red_cards_away || 0}
                      onChange={(e) => setEditingMatch({ ...editingMatch, red_cards_away: e.target.value })}
                      style={styles.input}
                      min="0"
                    />
                    {parseInt(editingMatch.red_cards_away || 0) > 0 && (
                      <div style={{ marginTop: '8px' }}>
                        <label style={{ ...styles.label, fontSize: '12px', color: '#666' }}>Nom(s) de joueur</label>
                        <input
                          type="text"
                          placeholder="Ex: Ahmed Ben Ali, Mohamed Trabelsi"
                          value={editingMatch.red_card_players_away || ''}
                          onChange={(e) => setEditingMatch({ ...editingMatch, red_card_players_away: e.target.value })}
                          style={{ ...styles.input, fontSize: '13px' }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div style={styles.cardsInfo}>
                  <p style={styles.cardsInfoText}>
                    💡 Règles: 3 cartons jaunes = 1 match de suspension | 1 carton rouge = 1 match de suspension
                  </p>
                  <p style={styles.cardsInfoText}>
                    👥 Entrez les noms des joueurs séparés par des virgules (ex: Ahmed Ben Ali, Mohamed Trabelsi)
                  </p>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Statut</label>
                <select
                  value={editingMatch.status}
                  onChange={(e) => setEditingMatch({ ...editingMatch, status: e.target.value })}
                  style={styles.input}
                >
                  <option value="upcoming">À venir</option>
                  <option value="live">En direct</option>
                  <option value="finished">Terminé</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Lieu (optionnel)</label>
                <input
                  type="text"
                  value={editingMatch.venue || ''}
                  onChange={(e) => setEditingMatch({ ...editingMatch, venue: e.target.value })}
                  style={styles.input}
                  placeholder="Stade..."
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Lien Live (YouTube ou Facebook)</label>
                <input
                  type="text"
                  value={editingMatch.youtube_url || ''}
                  onChange={(e) => {
                    const url = e.target.value;
                    const newStatus = (url && editingMatch.status === 'upcoming') ? 'live' : editingMatch.status;
                    setEditingMatch({ ...editingMatch, youtube_url: url, status: newStatus });
                  }}
                  style={styles.input}
                  placeholder="https://www.youtube.com/watch?v=... ou https://www.facebook.com/.../videos/..."
                />
              </div>

              <div style={styles.modalActions}>
                <button onClick={handleSave} style={styles.saveBtn}>
                  <FaCheck /> Enregistrer
                </button>
                <button onClick={() => setEditingMatch(null)} style={styles.cancelBtn}>
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Liste des matchs par journée (الجولات) */}
      <div style={styles.matchesCard}>
        <h2 style={styles.cardTitle}>
          {language === 'ar' ? `قائمة المباريات (${filteredMatches.length})` : `Liste des matchs (${filteredMatches.length})`}
        </h2>

        {filteredMatches.length === 0 ? (
          <div style={styles.emptyState}>
            <p>{language === 'ar' ? 'لا توجد مباريات لهذه الفلاتر' : 'Aucun match trouvé avec ces filtres'}</p>
          </div>
        ) : (
          <div style={styles.matchesList}>
            {rounds.map(roundNum => (
              <div key={roundNum} style={styles.roundBlock}>
                <h3 style={styles.roundTitle}>
                  {roundNum === 0
                    ? (language === 'ar' ? 'غير محدد' : 'Sans journée')
                    : (language === 'ar' ? `الجولة ${roundNum}` : `Journée ${roundNum}`)}
                  <span style={styles.roundCount}>({matchesByRound[roundNum].length})</span>
                </h3>
                {(matchesByRound[roundNum] || []).map(match => (
                  <div key={match.id} style={styles.matchCard}>
                    <div style={styles.matchDate}>
                      <FaCalendar style={styles.icon} />
                      <span>{new Date(match.match_date).toLocaleDateString(language === 'ar' ? 'ar-TN' : 'fr-FR')}</span>
                      <FaClock style={styles.icon} />
                      <span>{new Date(match.match_date).toLocaleTimeString(language === 'ar' ? 'ar-TN' : 'fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>

                    <div style={styles.matchTeams}>
                      <div style={styles.team}>
                        {match.home_team_logo && (
                          <img
                            src={match.home_team_logo.startsWith('http') ? match.home_team_logo : window.location.origin + match.home_team_logo}
                            alt=""
                            style={styles.teamLogo}
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        )}
                        <span style={styles.teamName}>{match.home_team_name}</span>
                      </div>

                      <div style={styles.score}>
                        {match.status === 'finished' ? (
                          <>
                            <span style={styles.scoreNum}>{match.home_score}</span>
                            <span style={styles.scoreSep}>-</span>
                            <span style={styles.scoreNum}>{match.away_score}</span>
                          </>
                        ) : (
                          <span style={styles.vs}>VS</span>
                        )}
                      </div>

                      <div style={styles.team}>
                        <span style={styles.teamName}>{match.away_team_name}</span>
                        {match.away_team_logo && (
                          <img
                            src={match.away_team_logo.startsWith('http') ? match.away_team_logo : window.location.origin + match.away_team_logo}
                            alt=""
                            style={styles.teamLogo}
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        )}
                      </div>
                    </div>

                    <div style={styles.matchFooter}>
                      <div style={styles.matchInfo}>
                        <span style={{
                          ...styles.statusBadge,
                          background: match.status === 'finished' ? '#28a745' :
                            match.status === 'live' ? '#dc3545' : '#ffc107'
                        }}>
                          {match.status === 'finished' ? 'Terminé' :
                            match.status === 'live' ? '🔴 En direct' : 'À venir'}
                        </span>
                        {match.leg && (
                          <span style={{
                            fontSize: '14px',
                            background: match.leg === 'retour' ? '#c41e3a' : '#2e7d32',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontWeight: 'bold',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }}>
                            {match.leg === 'retour' ? (language === 'ar' ? 'إياب (Retour)' : 'Retour') : (language === 'ar' ? 'ذهاب (Aller)' : 'Aller')}
                          </span>
                        )}
                        {match.round && <span style={styles.round}>J{match.round}</span>}
                        {match.status === 'finished' && (match.yellow_cards_home > 0 || match.yellow_cards_away > 0 || match.red_cards_home > 0 || match.red_cards_away > 0) && (
                          <div style={styles.cardsDisplay}>
                            {(match.yellow_cards_home > 0 || match.red_cards_home > 0) && (
                              <span
                                style={styles.cardsBadge}
                                title={
                                  (match.yellow_card_players_home || match.red_card_players_home)
                                    ? `${match.home_team_name}\n${match.yellow_card_players_home ? '🟨 ' + match.yellow_card_players_home : ''}${match.yellow_card_players_home && match.red_card_players_home ? '\n' : ''}${match.red_card_players_home ? '🟥 ' + match.red_card_players_home : ''}`
                                    : match.home_team_name
                                }
                              >
                                {match.yellow_cards_home > 0 && `🟨${match.yellow_cards_home}`}
                                {match.red_cards_home > 0 && ` 🟥${match.red_cards_home}`}
                              </span>
                            )}
                            {(match.yellow_cards_away > 0 || match.red_cards_away > 0) && (
                              <span
                                style={styles.cardsBadge}
                                title={
                                  (match.yellow_card_players_away || match.red_card_players_away)
                                    ? `${match.away_team_name}\n${match.yellow_card_players_away ? '🟨 ' + match.yellow_card_players_away : ''}${match.yellow_card_players_away && match.red_card_players_away ? '\n' : ''}${match.red_card_players_away ? '🟥 ' + match.red_card_players_away : ''}`
                                    : match.away_team_name
                                }
                              >
                                {match.yellow_cards_away > 0 && `🟨${match.yellow_cards_away}`}
                                {match.red_cards_away > 0 && ` 🟥${match.red_cards_away}`}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      <div style={styles.matchActions}>
                        {match.status !== 'finished' && (
                          <button
                            onClick={() => handleQuickFinish(match)}
                            style={styles.quickBtn}
                            title="Ajouter résultat rapide"
                          >
                            <FaTrophy /> Résultat
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(match)}
                          style={styles.editBtn}
                        >
                          <FaEdit /> {language === 'ar' ? 'تعديل' : 'Modifier'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  title: { fontSize: '32px', fontWeight: 'bold', marginBottom: '20px' },
  infoBox: { background: '#e3f2fd', border: '2px solid #2196f3', borderRadius: '10px', padding: '15px 20px', marginBottom: '30px', color: '#1565c0' },
  filtersCard: { background: 'white', padding: '25px', borderRadius: '15px', marginBottom: '30px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center' },
  filterGroup: { display: 'flex', alignItems: 'center', gap: '10px' },
  filterLabel: { fontSize: '14px', fontWeight: 'bold', color: '#666' },
  filterSelect: { padding: '8px 15px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', fontWeight: '600' },
  phaseToggle: { padding: '8px 20px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', transition: 'all 0.3s' },
  statsGroup: { marginLeft: 'auto', display: 'flex', gap: '20px' },
  stat: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
  statLabel: { fontSize: '12px', color: '#666' },
  statValue: { fontSize: '20px', fontWeight: 'bold', color: '#667eea' },
  matchesCard: { background: 'white', padding: '30px', borderRadius: '15px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' },
  cardTitle: { fontSize: '22px', fontWeight: 'bold', marginBottom: '25px' },
  matchesList: { display: 'flex', flexDirection: 'column', gap: '20px' },
  roundBlock: { marginBottom: '24px' },
  roundTitle: { fontSize: '18px', fontWeight: 'bold', color: '#c41e3a', marginBottom: '12px', paddingBottom: '8px', borderBottom: '2px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: '8px' },
  roundCount: { fontSize: '14px', fontWeight: 'normal', color: '#666' },
  matchCard: { background: '#f9f9f9', borderRadius: '12px', padding: '20px', border: '2px solid #e0e0e0', marginBottom: '12px' },
  matchDate: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px', color: '#666', fontSize: '14px' },
  icon: { color: '#667eea' },
  matchTeams: { display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '20px', alignItems: 'center', marginBottom: '15px' },
  team: { display: 'flex', alignItems: 'center', gap: '10px' },
  teamLogo: { width: '35px', height: '35px', objectFit: 'contain' },
  teamName: { fontSize: '16px', fontWeight: 'bold' },
  score: { display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' },
  scoreNum: { fontSize: '24px', fontWeight: 'bold', color: '#667eea' },
  scoreSep: { fontSize: '20px', color: '#999' },
  vs: { fontSize: '16px', fontWeight: 'bold', color: '#999' },
  matchFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '15px', borderTop: '1px solid #e0e0e0' },
  matchInfo: { display: 'flex', gap: '10px', alignItems: 'center' },
  statusBadge: { padding: '5px 15px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', color: 'white' },
  round: { fontSize: '12px', color: '#999', fontWeight: 'bold' },
  cardsDisplay: { display: 'flex', gap: '5px', marginTop: '5px', flexWrap: 'wrap' },
  cardsBadge: { fontSize: '11px', padding: '3px 8px', background: '#fff3cd', borderRadius: '12px', fontWeight: 'bold', color: '#856404', cursor: 'help', position: 'relative' },
  matchActions: { display: 'flex', gap: '10px' },
  quickBtn: { padding: '8px 15px', background: '#28a745', color: 'white', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' },
  editBtn: { padding: '8px 15px', background: '#007bff', color: 'white', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modalContent: { background: 'white', borderRadius: '15px', padding: '30px', maxWidth: '600px', width: '100%', maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { fontSize: '24px', fontWeight: 'bold', marginBottom: '20px', color: '#333' },
  matchHeader: { background: '#f5f5f5', padding: '15px', borderRadius: '10px', marginBottom: '20px' },
  teamDisplay: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', fontSize: '18px' },
  form: { display: 'flex', flexDirection: 'column', gap: '15px' },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
  label: { fontSize: '14px', fontWeight: 'bold', color: '#333' },
  input: { padding: '12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '15px' },
  legBtn: { flex: 1, padding: '10px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', transition: 'all 0.3s' },
  modalActions: { display: 'flex', gap: '10px', marginTop: '10px' },
  saveBtn: { flex: 1, padding: '15px', background: '#28a745', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
  cancelBtn: { padding: '15px 30px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' },
  cardsSection: { marginTop: '20px', padding: '20px', background: '#fff3cd', borderRadius: '10px', border: '2px solid #ffc107' },
  cardsSectionTitle: { fontSize: '18px', fontWeight: 'bold', marginBottom: '15px', color: '#856404' },
  cardsInfo: { marginTop: '15px', padding: '12px', background: '#e3f2fd', borderRadius: '8px' },
  cardsInfoText: { margin: 0, fontSize: '13px', color: '#1565c0', fontWeight: '500' },
  loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px', color: '#666' },
  spinner: { width: '50px', height: '50px', border: '5px solid #f3f3f3', borderTop: '5px solid #c41e3a', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  spinner: { width: '50px', height: '50px', border: '5px solid #f3f3f3', borderTop: '5px solid #c41e3a', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  emptyState: { textAlign: 'center', padding: '60px 20px', color: '#999' },
  pdfBtn: { padding: '10px 20px', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.3s' },
  pdfSection: { background: '#f8f9fa', padding: '20px', borderRadius: '15px', marginBottom: '30px', border: '1px solid #dee2e6' },
  pdfButtons: { display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '15px' },
  pdfGroupBtn: { padding: '8px 15px', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' },
  pdfFrameContainer: { background: 'white', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }
};


// Animation CSS
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  button:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
  [data-tooltip]:hover::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    margin-bottom: 5px;
    background: #333;
    color: white;
    padding: 8px 12px;
    border-radius: 8px;
    font-size: 11px;
    white-space: pre-line;
    z-index: 1000;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  }
`;
if (!document.getElementById('results-management-styles')) {
  styleSheet.id = 'results-management-styles';
  document.head.appendChild(styleSheet);
}

export default ResultsManagementSection;
